// shared/infrastructure/catalogos.repository.js  (catálogos del módulo; normaliza a { id, nombre })
import Api from '../../../../api/global.service';
import { unwrap } from './apiResponse';
import { unicosPorId } from '../domain/coleccion';

// Las respuestas pueden venir como array directo o envueltas en { data: [...] }.
const toList = body => (Array.isArray(body) ? body : (body && body.data) || []);
const parcelaLabel = item => [item.codigo, item.nombre].filter(Boolean).join(' · ') || `Parcela ${item.id}`;
const toOption = item => ({ id: Number(item.id), nombre: String(item.nombre || '').trim() });

// Productores (cuentas) por ciclo: promesa cacheada por idCiclo.
const productoresCache = new Map();

// Parcelas por productor (id_cuenta): promesa cacheada. NUNCA se descarga `parcelas_all` completo (~3 MB).
const parcelasCuentaCache = new Map();
// Usuarios de la cooperativa (solo id y nombre): promesa cacheada.
const usuariosCache = { promise: null };
// Localidades por municipio: se cachean (promesa) para que formularios y listados las reutilicen.
const localidadesCache = new Map();

export const catalogosRepository = {
  async actividades() {
    return toList(await unwrap(Api.getAll('at/catalogos/actividades'))).map(toOption);
  },

  async municipios() {
    return toList(await unwrap(Api.getAll('cooperativas_municipios'))).map(toOption);
  },

  localidades(idMunicipio) {
    if (!idMunicipio) return Promise.resolve([]);
    if (!localidadesCache.has(idMunicipio)) {
      const promise = unwrap(Api.getAll(`localidades_municipio_all/${idMunicipio}`)).then(body => toList(body).map(toOption));
      localidadesCache.set(idMunicipio, promise);
      // Si falla, se permite reintentar.
      promise.catch(() => localidadesCache.delete(idMunicipio));
    }
    return localidadesCache.get(idMunicipio);
  },

  // Productores del ciclo: [{ id, uuid, nombre, codigo, total_parcelas, localidad, municipio }]. Sin ciclo => sin filtro. Caché por ciclo.
  productores(idCiclo) {
    const key = idCiclo || 'todos';
    if (!productoresCache.has(key)) {
      const filter = idCiclo ? [{ column: 'id_ciclo', operator: '=', value: idCiclo }] : [];
      const promise = unwrap(Api.search('cuentas', { filter })).then(body =>
        toList(body).map(item => ({
          id: Number(item.id),
          nombre: item.nombre || `Productor ${item.id}`,
          total_parcelas: Number(item.total_parcelas) || 0,
          uuid: item.uuid || '',
          // Código del productor: codigo_tx (CÓDIGO en cuentas-list) y, si no hay, clave_tx (CLAVE).
          codigo: String(item.codigo_tx || item.clave_tx || '').trim(),
          localidad: item.localidad || '',
          municipio: item.municipio || '',
        }))
      );
      productoresCache.set(key, promise);
      promise.catch(() => productoresCache.delete(key));
    }
    return productoresCache.get(key);
  },

  // Todos los asistentes de la cooperativa (lista plana: id_capacitacion / id_visita numéricos, deleted_at).
  async asistentes() {
    return toList(await unwrap(Api.getAll('at/asistentes')));
  },

  async ciclos(idCooperativa) {
    const body = await unwrap(
      Api.search('ciclos', { filter: [{ column: 'id_cooperativa', operator: '=', value: idCooperativa }], acopio: 0 })
    );
    return toList(body).map(toOption);
  },

  // Parcelas de UN productor: [{ id, nombre }] con nombre "código · nombre". Caché por id_cuenta; si falla se puede reintentar.
  parcelasDeProductor(idCuenta) {
    if (!idCuenta) return Promise.resolve([]);
    if (!parcelasCuentaCache.has(idCuenta)) {
      const filter = [{ column: 'id_cuenta', operator: '=', value: idCuenta }];
      const promise = unwrap(Api.search('parcelas_all', { filter })).then(body =>
        // La vista repite la misma parcela una vez por ciclo: se deja una sola por id.
        unicosPorId(toList(body).map(item => ({ id: Number(item.id), nombre: parcelaLabel(item) })))
      );
      parcelasCuentaCache.set(idCuenta, promise);
      promise.catch(() => parcelasCuentaCache.delete(idCuenta));
    }
    return parcelasCuentaCache.get(idCuenta);
  },

  // Usuarios: [{ id, nombre }]. El endpoint devuelve más campos (incluso hashes): aquí se descartan TODOS menos id y nombre.
  // Si falla (p. ej. 403 para roles sin acceso) resuelve [] para no romper la pantalla; la UI muestra "Usuario {id}".
  usuarios() {
    if (!usuariosCache.promise) {
      usuariosCache.promise = unwrap(Api.search('users', { filter: [] })).then(
        body => toList(body).map(item => ({ id: Number(item.id), nombre: String(item.nombre || '').trim() })),
        () => {
          usuariosCache.promise = null;
          return [];
        }
      );
    }
    return usuariosCache.promise;
  },
};

export default catalogosRepository;
